import { createI18n } from 'vue-i18n'
import type { WritableComputedRef } from 'vue'
import enUS from './locales/en-US.json'
import {
  DEFAULT_LOCALE,
  resolveLanguage,
  type Locale,
} from './language'

export { DEFAULT_LOCALE, SUPPORTED_LANGS, normalizeLanguage, resolveLanguage, toAppLanguage } from './language'
export type { Locale } from './language'

/** 按需加载非默认语言包 */
export async function loadLocale(lang: Locale): Promise<Record<string, any>> {
  switch (lang) {
    case 'zh-CN': return (await import('./locales/zh-CN.json')).default
    case 'zh-TW': return (await import('./locales/zh-TW.json')).default
    case 'th-TH': return (await import('./locales/th-TH.json')).default
    default: return enUS
  }
}

/** 检测应使用的语言；WebView 环境能力异常时也必须安全回退英语。 */
export function detectLanguage(appLanguage?: string | null): Locale {
  let storedLanguage: string | null = null
  let browserLanguage: string | null = null
  try { storedLanguage = localStorage.getItem('i18nextLng') } catch { /* 无存储权限时忽略 */ }
  try { browserLanguage = navigator.language } catch { /* 无法读取系统语言时忽略 */ }
  return resolveLanguage(appLanguage, storedLanguage, browserLanguage)
}

const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    [DEFAULT_LOCALE]: enUS,
  },
})

/** 切换语言；目标语言包加载失败时继续使用内置英语包。 */
export async function setLanguage(lang: Locale): Promise<Locale> {
  let activeLanguage = lang
  if (lang !== DEFAULT_LOCALE) {
    try {
      const bundle = await loadLocale(lang)
      i18n.global.setLocaleMessage(lang, bundle as typeof enUS)
    } catch {
      activeLanguage = DEFAULT_LOCALE
    }
  }
  ;(i18n.global.locale as WritableComputedRef<Locale>).value = activeLanguage
  document.documentElement.lang = activeLanguage
  try { localStorage.setItem('i18nextLng', activeLanguage) } catch { /* 无存储权限时忽略 */ }
  return activeLanguage
}

export default i18n
